<template>
  <div class="fair-survey-step">
    <div class="fair-survey-step__head">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <div class="choice-step__list-head">
      <span class="fair-survey-step__eyebrow">Стили для оценки</span>
      <span
        v-if="totalPhotosCount > 0"
        class="choice-step__total"
      >
        {{ totalPhotosCount }} картинок
      </span>
    </div>

    <div class="fair-survey-step__options">
      <button
        v-for="option in step.options"
        :key="option.value"
        type="button"
        class="fair-survey-step__option"
        :class="{
          'fair-survey-step__option--selected': isStyleSelected(option.value),
        }"
        :aria-pressed="isStyleSelected(option.value)"
        @click="toggleStyle(option.value)"
      >
        <FairCheckboxIndicator :checked="isStyleSelected(option.value)" />

        <img
          class="choice-step__image"
          :src="getStyleImage(option)"
          :alt="option.label"
        >

        <span class="choice-step__text">
          <span class="choice-step__name">{{ option.label }}</span>
          <span
            v-if="option.percentage !== undefined"
            class="fair-survey-step__card-meta"
          >
            {{ formatProgress(option.percentage) }}
          </span>
        </span>

        <span
          v-if="option.photosCount !== undefined"
          class="choice-step__badge"
        >
          +{{ option.photosCount }}
        </span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { FairCheckboxIndicator } from '@/ui/primitives/FairCheckbox'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'
import { getStyleImagePath } from '@/features/taste/utils/style-images'
import { mapStyleImageFromBackend } from '@/utils/style-image-mapper'

interface Props {
  step: TasteStep & { type: 'choice' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedStyles = computed(() => {
  const value = tasteStore.tasteModal.tasteData[props.step.id]
  if (props.step.multiSelect) {
    return Array.isArray(value) ? value : []
  }
  return value ? [value] : []
})

const isStyleSelected = (styleValue: string): boolean => {
  return Array.isArray(selectedStyles.value) && selectedStyles.value.includes(styleValue)
}

const totalPhotosCount = computed(() => {
  if (!Array.isArray(selectedStyles.value)) return 0
  return props.step.options
    .filter(option => selectedStyles.value.includes(option.value))
    .reduce((sum, option) => sum + (option.photosCount || 0), 0)
})

const canContinue = computed(() => {
  if (!props.step.multiSelect) return true
  // Если шаг не обязателен, можно продолжить в любом случае
  if (!props.step.required) return true
  // Если шаг обязателен, нужно выбрать хотя бы одну опцию
  return Array.isArray(selectedStyles.value) && selectedStyles.value.length > 0
})

function toggleStyle(styleValue: string) {
  if (!props.step.multiSelect) {
    // Для одиночного выбора просто сохраняем значение
    tasteStore.updateTasteData(props.step.id, styleValue)
    return
  }

  // Для множественного выбора добавляем/удаляем из массива
  // Читаем текущие значения напрямую из store для гарантии актуальности
  const currentValue = tasteStore.tasteModal.tasteData[props.step.id]
  const currentValues = Array.isArray(currentValue) ? [...currentValue] : []
  const index = currentValues.indexOf(styleValue)
  
  if (index > -1) {
    currentValues.splice(index, 1)
  } else {
    currentValues.push(styleValue)
  }

  tasteStore.updateTasteData(props.step.id, currentValues)
}

function getStyleImage(option: { value: string; image?: string; code?: string }): string {
  // Если в опции есть изображение, применяем маппинг из бэкенда
  if (option.image) {
    // Применяем маппинг, используя code если есть, иначе value как потенциальный код
    return mapStyleImageFromBackend({
      code: option.code || option.value,
      image: option.image
    })
  }
  // Иначе используем путь из утилиты
  return getStyleImagePath(option.value)
}

function formatProgress(percentage: number): string {
  return `Прогресс: ${percentage}%`
}

// Автоматически выбираем стили из style-selection-step при монтировании
// Только для шага style-evaluation-choice
onMounted(() => {
  // Проверяем, что это шаг style-evaluation-choice
  if (props.step.id !== 'style-evaluation-choice') return
  
  // Находим шаг style_selection в конфигурации
  const styleSelectionStep = tasteStore.steps.find(step => step.type === 'style_selection')
  
  if (!styleSelectionStep) return
  
  // Получаем выбранные стили из tasteData для шага style_selection
  const selectedStylesFromSelection = tasteStore.tasteModal.tasteData[styleSelectionStep.id]
  
  if (!selectedStylesFromSelection || !Array.isArray(selectedStylesFromSelection)) return
  
  // Нормализуем ID к числам
  const selectedStyleIdsFromSelection = selectedStylesFromSelection
    .map((id: any) => typeof id === 'number' ? id : parseInt(id, 10))
    .filter((id: any) => !isNaN(id))
  
  if (selectedStyleIdsFromSelection.length === 0) return
  
  // Находим опции из step.options, которые соответствуют выбранным на style-selection-step
  // option.value - это строка (ID стиля как строка)
  const optionsToSelect = props.step.options
    .filter(option => {
      const optionId = parseInt(option.value, 10)
      return !isNaN(optionId) && selectedStyleIdsFromSelection.includes(optionId)
    })
    .map(option => option.value)
  
  if (optionsToSelect.length === 0) return
  
  // Проверяем, есть ли уже сохраненные данные для текущего шага
  const currentData = tasteStore.tasteModal.tasteData[props.step.id]
  
  // Если данных еще нет или массив пустой, автоматически выбираем все найденные опции
  if (!currentData || (Array.isArray(currentData) && currentData.length === 0)) {
    tasteStore.updateTasteData(props.step.id, optionsToSelect)
  }
})
</script>

<style scoped>
.choice-step__list-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--fair-rd-space-12);
}

.choice-step__total {
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 13px;
  line-height: 1;
  color: var(--fair-rd-text-muted);
  font-variant-numeric: tabular-nums;
}

.choice-step__image {
  flex: none;
  width: 44px;
  height: 56px;
  border-radius: var(--fair-rd-radius-control);
  object-fit: cover;
}

.choice-step__text {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--fair-rd-space-4);
}

.choice-step__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* How many more cards this style adds to the deck. */
.choice-step__badge {
  flex: none;
  padding: var(--fair-rd-space-4) var(--fair-rd-space-8);
  border-radius: var(--fair-rd-radius-full);
  background: var(--fair-rd-fill-chip);
  font-family: var(--fair-font-body, Inter, sans-serif);
  font-size: 12px;
  line-height: 1;
  color: var(--fair-rd-text-secondary);
  font-variant-numeric: tabular-nums;
}
</style>
